import { HISTORY_ACTION_MODE, NodeId } from '@candulabs/craft';
import invariant from 'invariant';
import { useMemo } from 'react';

import {
  useIsUsingDefaultCssControls,
  useViewportBreakpoint,
} from 'craft/state/viewport/selectors';
import {
  CssSelectorId,
  getResponsiveSelectorIds,
  isBaseSelectorId,
  MediaBreakpoint,
} from 'craft/types';

import { useMultiCssSelector } from './useMultiCssSelector';
import { useMultiNodes } from './useMultiNodes';

export const useBaseCss = (
  nodeIds: NodeId[],
  commonProperty?: any,
  cssSelectorId: CssSelectorId = CssSelectorId.Root,
) => {
  const {
    css: plainCss,
    actions: { setCss: setPlainCss },
  } = useMultiNodes(nodeIds, (node) => ({ css: node?.data?.custom?.css ?? {} }), commonProperty);
  const { css: baseCss, setCssSelector: setBaseCss } = useMultiCssSelector(
    nodeIds,
    cssSelectorId,
    commonProperty,
  );

  // for the time being, if you're setting the root css we're just setting the regular css object
  // instead of using a css selector.
  // this is just to avoid migrations as some objects and parts of the code base still rely on this object, for examle the Modal and Document components.
  if (cssSelectorId === CssSelectorId.Root) {
    return { css: plainCss, setCss: setPlainCss };
  }
  return { css: baseCss, setCss: setBaseCss };
};

export const useResponsiveCss = (
  nodeIds: NodeId[],
  commonProperty?: any,
  cssSelectorId: CssSelectorId = CssSelectorId.Root,
) => {
  invariant(
    isBaseSelectorId(cssSelectorId),
    "(useResponsiveCss) You're trying to use a responsive hook with a non base css selector",
  );
  const isUsingDefaultCssControls = useIsUsingDefaultCssControls();
  const viewportBreakpoint = useViewportBreakpoint();
  const viewportSelectors = getResponsiveSelectorIds(cssSelectorId);
  const { css, setCss } = useBaseCss(nodeIds, commonProperty, cssSelectorId);
  const { css: mdCss, setCssSelector: setMdCssSelector } = useMultiCssSelector(
    nodeIds,
    viewportSelectors[MediaBreakpoint.MD],
    commonProperty,
  );
  const { css: smCss, setCssSelector: setSmCssSelector } = useMultiCssSelector(
    nodeIds,
    viewportSelectors[MediaBreakpoint.SM],
    commonProperty,
  );

  const responsiveCss = useMemo(() => {
    const safeCss = css ?? {};
    if (viewportBreakpoint === MediaBreakpoint.LG) {
      return safeCss;
    }
    if (viewportBreakpoint === MediaBreakpoint.MD) {
      return { ...safeCss, ...mdCss };
    }
    return { ...safeCss, ...mdCss, ...smCss };
  }, [css, mdCss, smCss, viewportBreakpoint]);

  const setResponsiveCss = (
    newCss: object,
    historyMode?: HISTORY_ACTION_MODE,
    throttleNumber?: number,
  ) => {
    if (isUsingDefaultCssControls) {
      setCss(newCss, historyMode, throttleNumber);
    } else if (viewportBreakpoint === MediaBreakpoint.MD) {
      setMdCssSelector(newCss, historyMode, throttleNumber);
    } else {
      setSmCssSelector(newCss, historyMode, throttleNumber);
    }
  };

  return { responsiveCss, setResponsiveCss };
};
